import React, { useEffect, useRef } from 'react';
import type { AiJob } from '../../lib/aiJobs';
import { handoffByline, handoffEntries } from '../../lib/aiJobHandoff';
import { Button, Icon, IconButton } from '../ui';
import AiMarkdown from './AiMarkdown';

/**
 * The optional note that rides with a forward click (Approve spec / Approve
 * build): a reminder for whichever agent picks the job up next. It never sends
 * the job back. `cockpit ai get` prints it "with the go-ahead".
 */

const HANDOFF_NOTE_LABEL = 'Note for the agent (optional)';
const HANDOFF_NOTE_PLACEHOLDER =
  'A reminder for whoever picks this up — e.g. only use the CLI when updating the CMS';

/** Labelled textarea. Pass `onRemove` to show an x that clears and folds it away. */
export function HandoffNoteField({
  value,
  onChange,
  disabled = false,
  onRemove,
  autoFocus = false,
  rows = 2,
  style,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  onRemove?: () => void;
  autoFocus?: boolean;
  rows?: number;
  style?: React.CSSProperties;
}) {
  // Focus once when the field appears. The textarea's own autoFocus fought the operator panel's focus
  // handling and looped ("Maximum update depth exceeded"); one deferred focus() doesn't.
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!autoFocus) return;
    const t = window.setTimeout(() => ref.current?.focus({ preventScroll: true }), 0);
    return () => window.clearTimeout(t);
    // Only on mount: re-focusing on every render is what the loop was.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0, ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minHeight: '22px' }}>
        <Icon name="sticky-note" size={13} style={{ color: 'var(--text-faint)', flexShrink: 0 }} />
        <span className="t-eyebrow" style={{ flex: 1, minWidth: 0 }}>
          {HANDOFF_NOTE_LABEL}
        </span>
        {/* control-sm less 6: 22px on desktop as before, 30px on touch screens. */}
        {onRemove ? (
          <IconButton icon="x" size="sm" label="Remove note" disabled={disabled} onClick={onRemove} style={{ width: 'calc(var(--control-sm) - 6px)', height: 'calc(var(--control-sm) - 6px)' }} />
        ) : null}
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={HANDOFF_NOTE_PLACEHOLDER}
        rows={rows}
        className="ck-textarea"
        disabled={disabled}
        ref={ref}
        aria-label={HANDOFF_NOTE_LABEL}
        style={{ resize: 'vertical', minHeight: '56px', maxHeight: '30dvh' }}
      />
      <span style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)', lineHeight: 'var(--leading-snug)' }}>
        Goes with the go-ahead. Doesn’t send the job back.
      </span>
    </div>
  );
}

/** The small link that opens the field where space is tight. */
export function AddHandoffNoteButton({ onClick, disabled = false }: { onClick: () => void; disabled?: boolean }) {
  return (
    <Button size="sm" variant="ghost" icon="plus" disabled={disabled} onClick={onClick} title="Add a reminder for the agent that picks this up next">
      Add a note
    </Button>
  );
}

/** Read-only card listing every note a job carries, oldest first (operator panel). They stack: nothing clears them. */
export function HandoffNoteCard({ job }: { job: Pick<AiJob, 'handoff_notes'> }) {
  const entries = handoffEntries(job);
  if (!entries.length) return null;
  return (
    <div
      style={{
        padding: '12px 16px',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--accent-soft)',
        border: '1px solid var(--border-hairline)',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Icon name="sticky-note" size={13} style={{ color: 'var(--accent-text)', flexShrink: 0 }} />
        <span className="t-eyebrow" style={{ color: 'var(--accent-text)' }}>
          {entries.length === 1 ? 'Note with the go-ahead' : `Notes with the go-ahead · ${entries.length}`}
        </span>
      </div>
      {entries.map((e, i) => (
        <div key={`${e.at}-${i}`} style={{ borderTop: i ? '1px solid var(--border-hairline)' : 'none', paddingTop: i ? '10px' : 0 }}>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: '4px' }}>{handoffByline(e)}</div>
          <AiMarkdown content={e.note} />
        </div>
      ))}
    </div>
  );
}
